import React, { useState } from 'react';
import { View, Text, Button, Textarea } from '@tarojs/components';
import * as Taro from '@tarojs/taro';
import Modal from '@/components/Modal';
import SettingsPanel from '@/components/SettingsPanel';
import { GameSettings } from '@/data/settings';
import { getGameStats, getLevelProgress, formatTime, getSettings, saveSettings, exportRecords, importRecords, getModeProgress } from '@/utils/storage';
import styles from './index.module.scss';
import { downloadBackup, importBackupFile, downloadCsv } from '@/web/files';

const MinePage: React.FC = () => {
  const [modal, setModal] = useState<'records' | 'guide' | 'settings' | 'backup' | 'about' | null>(null);
  const [backupText, setBackupText] = useState('');
  const [stats, setStats] = useState(getGameStats);
  const [progress, setProgress] = useState(getLevelProgress);
  const [settings, setSettings] = useState(getSettings);
  const [modes, setModes] = useState(getModeProgress);
  Taro.useDidShow(() => { setStats(getGameStats()); setProgress(getLevelProgress()); setSettings(getSettings()); setModes(getModeProgress()); });
  const update = (next: GameSettings) => {
    if (saveSettings(next)) setSettings(next);
    else Taro.showToast({ title: '设置保存失败，请重试', icon: 'none' });
  };
  const best = [...Object.values(progress.bestTimes), ...Object.values(modes.dailyBest), ...Object.values(modes.worldBest)];
  const recordItems = [['通关次数', stats.totalWins], ['最快用时', best.length ? formatTime(Math.min(...best)) : '—'],
    ['最长连胜', stats.longestWinStreak], ['当前连胜', stats.currentWinStreak]];
  const names = { records: '游戏记录', guide: '游戏说明', settings: '游戏设置', backup: '记录备份', about: '关于这个游戏' };
  return (
    <View className={styles.container}>
      <View className={styles.profile}><Text className={styles.avatar}>🐑</Text><View><Text className={styles.nickname}>羊羊玩家</Text><Text className={styles.signature}>慢慢规划，一起消除。</Text></View></View>
      <View className={styles.card}>
        <Text className={styles.heading}>挑战进度</Text>
        <View className={styles.check}><Text>每日一关</Text><Text className={styles.time}>已通关 {modes.dailyDates.length} 天</Text></View>
        <View className={styles.check}><Text>羊羊大世界</Text><Text className={styles.time}>已过 {modes.worldCompleted.length} 关 · 当前第 {modes.worldStage} 关</Text></View>
      </View>
      <View className={styles.summary}>
        {recordItems.slice(0, 3).map(([label, value]) => <View key={label}><Text className={styles.value}>{value}</Text><Text className={styles.label}>{label}</Text></View>)}
      </View>
      <View className={styles.menu}>
        {(Object.keys(names) as (keyof typeof names)[]).map((key, index) => <View key={key} className={styles.menuItem} onClick={() => setModal(key)} data-menu={key}>
          <Text>{['📊', '📖', '⚙️', '💾', '🐑'][index]} {names[key]}</Text><Text className={styles.arrow}>›</Text>
        </View>)}
      </View>
      {modal && <Modal title={names[modal]} onClose={() => setModal(null)}>
        {modal === 'records' && <View className={styles.records}>
          {recordItems.map(([label, value]) => <View key={label} className={styles.record}><Text className={styles.label}>{label}</Text><Text className={styles.recordValue}>{value}</Text></View>)}
          <Text className={styles.description}>有效用时不含暂停、答题及后台停留时间。</Text>
        </View>}
        {modal === 'guide' && <View className={styles.guide}>
          <Text className={styles.heading}>三张消除</Text><Text>点击亮着且未被遮挡的牌，将相同图案凑齐三张。第七张若刚好三消，会继续游戏；消除后卡槽仍有七张才失败。</Text>
          <Text className={styles.heading}>移出与暂存</Text><Text>移出卡槽最前面的三张牌。暂存区的牌不会丢弃，可以点回卡槽继续消除；暂存牌也全部消除才算通关。</Text>
          <Text className={styles.heading}>撤回与洗牌</Text><Text>撤回上一步，包括那一步三消。洗牌只重新分配场上图案，卡槽与暂存区不动。主道具默认各一次，可在设置里修改。</Text>
          <Text className={styles.heading}>满槽复活</Text><Text>复活会把卡槽前三张移到暂存区，继续同一局；不会重新发牌，也不会重置其他道具。</Text>
          <Text className={styles.heading}>答题与自定义</Text><Text>可选无需答题、用完后答题补给，或每次使用前答题。答错显示解析，可以下一题继续；取消不扣道具。无道具挑战会禁用道具、复活及答题奖励。</Text>
          <Text className={styles.heading}>保存设置</Text><Text>模式、难度、初始次数和题库规则在下一局生效；声音、动画和剩余张数开关即时生效。切后台自动暂停。</Text>
        </View>}
        {modal === 'settings' && <SettingsPanel value={settings} onChange={update} />}
        {modal === 'backup' && <View className={styles.guide}>
          <Text>通关、解锁、最好用时和设置会自动保存在本机。本局也会自动保存，重新进入后从暂停处继续。</Text>
          <Text className={styles.description}>普通代码更新沿用原记录。换手机、换网址或清除浏览器数据前，请下载JSON备份并另存。备份文本包含累计记录和设置，本局牌面只在本机保存。</Text>
          <Button className={styles.backupButton} onClick={() => {
            const text = exportRecords(); setBackupText(text);
            Taro.setClipboardData({ data: text }).catch(() => Taro.showToast({ title: '可手动复制下方文本', icon: 'none' }));
          }}
          >复制记录备份</Button><Button className={styles.backupButton} onClick={downloadBackup}>下载 JSON 备份</Button><Button className={styles.backupButton} onClick={downloadCsv}>导出成绩 CSV</Button><Button className={styles.backupButton} onClick={async () => { const text = await importBackupFile(); if (text) setBackupText(text); }}>选择 JSON 备份文件</Button>
          <Textarea className={styles.backupInput} maxlength={-1} value={backupText} placeholder='在这里粘贴之前保存的备份文本' onInput={event => setBackupText(event.detail.value)} />
          <Button className={styles.backupButton} disabled={!backupText.trim()} onClick={() => {
            const ok = importRecords(backupText);
            if (ok) { setStats(getGameStats()); setProgress(getLevelProgress()); setSettings(getSettings()); setModes(getModeProgress()); }
            Taro.showToast({ title: ok ? '记录已合并，设置已恢复' : '备份无效或保存失败', icon: 'none' });
          }}
          >合并记录并恢复备份设置</Button>
          <Text className={styles.description}>合并会保留更多的解锁与通关记录、较快的用时；恢复旧备份不会减少这些记录。</Text>
        </View>}
        {modal === 'about' && <View className={styles.guide}><Text className={styles.heading}>羊了个羊 · 自用练习</Text><Text>设置和记录保存在当前浏览器。无需账号登录、广告或分享。</Text><Text className={styles.description}>这是原羊小程序的独立网页版。进度保存在当前浏览器，不跨设备同步；小程序和网页版分别保存。素材来源保存在项目的sources.json中。</Text></View>}
      </Modal>}
    </View>
  );
};
export default MinePage;
